<template>
  <div v-bind:class="{'spinner-is-visible': isSpinnerVisible }" style="min-height: 300px;">
    <div v-bind:class="{'hidden': !isLoaded }">
      <div class="row">
        <div class="col-lg-6">
          <fieldset>
            <legend>Project</legend>
            <div class="row">
              <div class="col-sm-12 col-xs-12">
                <div class="form-group">
                  <label for="fulcrum_app_name" class="placeholder placeholder-static">Fulcrum App name</label>
                  <input 
                    class="form-control"
                    type="text" 
                    name="fulcrum_app[name]" 
                    id="fulcrum_app_name"
                    readonly
                    v-model.trim="fulcrumAppName"
                  />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-12 col-xs-12">
                <div class="form-group">
                  <label for="fulcrum_app_label" class="placeholder placeholder-static">Project number</label>
                  <input 
                    :class="['form-control', {'error': v$.fulcrumAppLabel.$error}]"
                    placeholder="Label" 
                    type="text" 
                    name="fulcrum_app[label]" 
                    id="fulcrum_app_label"
                    v-model.trim="fulcrumAppLabel"
                    @keydown.enter.prevent="submitForm"
                  />
                  <div v-if="v$.fulcrumAppLabel.$error" class="error">Project number is required</div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-12 col-xs-12">
                <div class="form-group">
                  <label class="placeholder placeholder-inactive">Client Company</label>
                  <v-select
                    placeholder="Select client company"
                    ref="clientCompanies"
                    :class="[{'vs-error': v$.clientCompanyId.$error}]"
                    :options="clientCompanies"
                    label="name"
                    @update:modelValue="setClientCompany"
                  />
                  <input 
                    type="hidden" 
                    id="fulcrum_app_client_company_id" 
                    name="fulcrum_app[client_company_id]"
                    :value="clientCompanyId"
                  />
                  <div v-if="v$.clientCompanyId.$error" class="error">Client company is required</div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-12">
                <div class="form-group">
                  <div class="form-control-toggle toggle-inline toggle-left toggle-warning d-inline-flex">
                    <input
                      type="radio"
                      name="fulcrum_app[status]"
                      id="fulcrum_app_status_ongoing"
                      autocomplete="off"
                      value="ongoing"
                      :checked="!projectCompleted"
                      @change="projectCompleted = false"
                    />
                    <input
                      type="radio"
                      name="fulcrum_app[status]"
                      id="fulcrum_app_status_completed"
                      autocomplete="off"
                      value="complete"
                      :checked="projectCompleted"
                      @change="projectCompleted = true"
                    />
                    <label :for="projectCompleted ? 'fulcrum_app_status_ongoing' : 'fulcrum_app_status_completed'" :title="projectCompleted ? 'Set this project as ongoing' : 'Set this project as completed'">
                      <span>{{ projectCompleted ? 'Completed' : 'Ongoing' }} project</span>
                      <span></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
        <div class="col-lg-6">
          <fieldset>
            <legend>Dashboard</legend>
            <div class="row">
              <div class="col-sm-6 col-xs-12">
                <div class="form-group">
                  <label class="placeholder placeholder-inactive">Dashboard Type</label>
                  <v-select
                    placeholder="Select Dashboard Type"
                    ref="dashboardTypes"
                    :class="[{'vs-error': v$.fulcrumAppDashboardType.$error}]"
                    :options="fulcrumAppDashboardTypes"
                    label="label"
                    @update:modelValue="setFulcrumAppDashboardType"
                  />
                  <input 
                    type="hidden"
                    id="fulcrum_app_dashboard_type" 
                    name="fulcrum_app[dashboard_type]"
                    :value="fulcrumAppDashboardType"
                  />
                  <div v-if="v$.fulcrumAppDashboardType.$error" class="error">Dashboard Type is required</div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-12 col-xs-12">
                <div class="form-group">
                  <label class="placeholder placeholder-inactive">Dashboard Groups</label>
                  <draggable 
                    ref="dashboardGroupsList"
                    v-model="fulcrumAppDashboardGroups"
                    handle=".data-type--handler"
                    group="dashboardGroups" 
                    @start="drag=true" 
                    @end="drag=false"
                  >
                    <div 
                      v-for="(group, index) in fulcrumAppDashboardGroups" 
                      :key="index"
                      class="form-field--data-type is-draggable"
                      :class="{'is-dragging': drag}"
                    >
                      <div class="data-type--form-group">
                        <div class="data-type--handler"></div>
                        <div class="data-type--label" style="width: auto;">
                          <span class="label label-active text-size-base ms-2" style="text-transform: capitalize; font-weight: normal;">{{ group.label }}</span>
                          <span class="label label-undefined text-size-base" style="font-weight: normal;">
                            <span class="text-color-dark">{{ index + 1 }}</span>
                          </span>
                        </div>
                      </div>
                      <input 
                        type="hidden"
                        id="fulcrum_app_dashboard_groups_" 
                        name="fulcrum_app[dashboard_groups][]"
                        :value="group.data_name"
                      />
                    </div>
                  </draggable>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
      </div>
      <div class="form-group">
        <form-button
          ref="submitButton"
          :label="submitButtonLabel"
          @submit="submitForm"
        ></form-button>
      </div>
    </div>
    <div class="spinner"></div>
  </div>
</template>

<script>
import vSelect from "vue-select";

import { VueDraggableNext } from 'vue-draggable-next'

import formButton from "../shared/form/form_button";

import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

export default {
  components: {
    draggable: VueDraggableNext,
    vSelect,
    formButton
  },

  props: {
    fulcrumFormId: String
  },

  setup () {
    return {
      v$: useVuelidate()
    }
  },

  data() {
    return {
      fulcrumApp: {
        name: '',
        statusField: {},
        fields: [],
        dashboardGroups: [],
      },
      clientCompanies: this.clientCompanies,
      clientCompany: null,
      clientCompanyId: '',
      fulcrumAppLabel: '',
      fulcrumAppName: '',
      projectCompleted: false,
      fulcrumAppDashboardGroups: [],
      fulcrumAppDashboardTypes: [
        { label: 'Wood Poles', value: 'wood_poles' },
        { label: 'Padmounts', value: 'padmounts' },
      ],
      fulcrumAppDashboardType: '',
      isLoaded: false,
      isSpinnerVisible: true,
    }
  },

  validations () {
    return {
      fulcrumAppLabel: {
        required
      },
      clientCompanyId: {
        required
      },
      fulcrumAppDashboardType: {
        required
      }
    }
  },

  computed: {
    submitButtonLabel: function() {
      return (this.isExistingfulcrumApp) ? "Update Dashboard" : "Create Dashboard";
    },

    isExistingfulcrumApp: function() {
      return this.fulcrumFormId != "";
    }
  },

  mounted: function() {
    this.fetchClientCompanies();

    if (this.isExistingfulcrumApp) {
      this.fetchfulcrumApp();
    }
    
    this.isLoaded = true;

    setTimeout(() => {
      this.isSpinnerVisible = false;
    }, 1000);
  },

  methods: {
    setProjectCompleted: function($event) {
      this.projectCompleted = $event.target.checked;
    },

    setFulcrumAppDashboardType: function(opt) {
      this.fulcrumAppDashboardType = (opt) ? opt.value : '';
    },

    setClientCompany: function(opt) {
      if (opt) {
        this.clientCompany = opt;
        this.clientCompanyId = opt.id;
      }
      else {
        this.clientCompany = null;
        this.clientCompanyId = '';
      }
    },

    fetchfulcrumApp: function(callback) {
      app.graphql.get(
        "details.fulcrumApp",

        {
          filter: {
            formId: this.fulcrumFormId
          }
        },

        (data) => {
          this.isLoaded = true

          this.fulcrumApp = data.data.fulcrumApps.edges[0].node;

          this.fulcrumApp.statusField = JSON.parse(this.fulcrumApp.statusField) || {};
          this.fulcrumApp.fields = this.fulcrumApp.fields.map((field) => {return JSON.parse(field) });

          this.fulcrumAppName = this.fulcrumApp.name;
          
          this.fulcrumAppLabel = this.fulcrumApp.label;

          this.projectCompleted = (this.fulcrumApp.status === 'complete');

          this.fulcrumAppDashboardGroups = this.fulcrumApp.dashboardGroups.map(name => {
            const field = this.fulcrumApp.fields.find(item => item.data_name === name);
            
            return field ? { 'data_name': name, 'label': field.label } : { 'data_name': name, 'label': name };
          });

          this.fulcrumAppDashboardType = this.fulcrumApp.dashboardType;

          if (this.fulcrumAppDashboardType) {
            this.$refs.dashboardTypes.updateValue({ 
              value: this.fulcrumAppDashboardType, 
              label: this.fulcrumAppDashboardTypes.find(type => type.value === this.fulcrumAppDashboardType)?.label }
            );
          }

          this.clientCompany = this.fulcrumApp.clientCompany;

          if (this.clientCompany && Object.keys(this.clientCompany).length > 0) {
            this.$refs.clientCompanies.updateValue(this.clientCompany);
          }

          setTimeout(() => {
            this.isSpinnerVisible = false
          }, 500)
        },

        (error) => {
          app.ui.toast.add({
            priority: "danger",
            title : "Project Dashboard Details",
            message : "Something went wrong! Project Dashboard cannot be found or doesn't exist!"
          })
        }
      );
    },

    fetchClientCompanies: function () {
      let fetchParams = {
         orderBy: {
          field: "NAME",
          direction: "ASC"
        }
      };

      this.clientCompanies = [];

      let currentPage = 1,
          maxPage = 100;

      let fetch = (cursor, success) => {
        fetchParams.cursor = cursor;

        app.graphql.get("select.clientCompanies",
          fetchParams,

          (data) => {
            let clientCompanies = data.data.clientCompanies,
                pageInfo = clientCompanies.pageInfo;

            success(clientCompanies, pageInfo);
          },

          (error) => {
            app.ui.toast.add({
              priority: "danger",
              title: "Something went wrong!",
              message: "Unable to fetch Client Companies!"
            });
          }
        )
      };

      let getClientCompanies = (cursor) => {
        fetch(
          cursor,

          (clientCompanies, pageInfo) => {
            clientCompanies.edges.forEach((edge) => this.clientCompanies.push(edge.node))

            if (pageInfo.hasNextPage && pageInfo.endCursor && currentPage++ < maxPage) {
              Promise.resolve(getClientCompanies(pageInfo.endCursor))
            }
          }
        )
      };

      getClientCompanies(window.btoa(0));
    },
    
    submitForm: function() {
      let form = document.getElementsByTagName("form")[0];

      this.v$.fulcrumAppLabel.$touch();
      this.v$.clientCompanyId.$touch();
      this.v$.fulcrumAppDashboardType.$touch();

      if (!this.v$.$error) {
        this.$refs.submitButton.loadingOn();
        
        form.submit();
      }
    }
  }
}

</script>
